/**
 * The order summary.
 *
 * ── WHERE THE GRADIENT GOES ───────────────────────────────────────────────
 * The gateway's hero is a gradient card and it earns that on a page whose job
 * is to make a first impression. Checkout's job is the opposite: the buyer has
 * already decided, and a second full-width hero would be decoration on a page
 * where every pixel should be reassurance. So the gradient appears exactly
 * once, on the panel that carries the money, which is the one element that
 * has to hold the eye from step one to step three. Same recipe, quarter the
 * area: `linear-gradient(112deg,#360B17,#4A1020 55%,#280D15)` with the crimson
 * bloom at 80% 28%.
 *
 * ── MONTHLY IS THE HERO, AT EVERY STEP, ON EITHER CADENCE ─────────────────
 * $400 is the figure at display size. The per-student equivalent is the line
 * under it. The annual total is third. That order does not change when the
 * buyer picks annual billing, because the reason for the order is sticker
 * shock and picking annual does not make $4,000 a smaller number.
 *
 * What DOES change is the "Charged today" row, which is the last row and
 * always states the real amount that will actually leave the institution's
 * account. The presentation hierarchy is a hierarchy, not a hiding place: a
 * buyer who is about to be charged $4,000 is told $4,000 before they click.
 *
 * ── STICKY, AND WHY IT IS ALSO ABOVE THE FORM ON A PHONE ──────────────────
 * On a wide screen it sits in a sticky column beside the steps. Below 1024px
 * it moves ABOVE the form rather than below it, because the answer to "what
 * am I paying" should not be a scroll away on the screen where the buyer is
 * typing their institution's legal name.
 */

import { formatCount, formatMoney } from "~/app/lib/format";
import { cn } from "~/app/lib/cn";
import {
  TERM_MONTHS,
  describeEquivalence,
  type CapacityBand,
} from "~/app/lib/pricing";
import type { Billing, CheckoutPath } from "~/app/components/checkout/checkout-model";

export interface OrderSummaryProps {
  band: CapacityBand;
  billing: Billing;
  onBillingChange: (billing: Billing) => void;
  path: CheckoutPath;
  estimatedStudents: number | null;
  /** Off on the confirmation screen, where the order is history rather than a choice. */
  editable?: boolean;
  className?: string;
}

export function OrderSummary({
  band,
  billing,
  onBillingChange,
  path,
  estimatedStudents,
  editable = true,
  className,
}: OrderSummaryProps) {
  const quoted = band.monthly === null || band.annual === null;

  return (
    <aside
      className={cn("eg-order", className)}
      aria-labelledby="order-heading"
    >
      <h2 id="order-heading" className="eg-order-eyebrow">
        Your subscription
      </h2>

      {/* ---- the figure ---- */}
      {quoted ? (
        <div className="eg-order-figure">
          <div className="eg-order-lead">
            <span className="eg-order-money">Quote</span>
          </div>
          <p className="eg-order-annual">Priced on your cohort.</p>
        </div>
      ) : (
        <div className="eg-order-figure">
          <div className="eg-order-lead">
            <span className="eg-order-money">{formatMoney(band.monthly ?? 0)}</span>
            <span className="eg-order-unit">per month</span>
          </div>
          {/* In the cadence picked below, as on /pricing (Sep 14 2026):
              "$2.67 per student, per month" or "$27 per student, per year". */}
          <p className="eg-order-per">{describeEquivalence(band, billing)}</p>
        </div>
      )}

      {/* ---- the cadence ---- */}
      {quoted ? null : (
        <div className="eg-order-cadence">
          <p id="order-cadence-label" className="e-sr-only">
            How you would like to be billed
          </p>
          <div className="eg-order-toggle" role="group" aria-labelledby="order-cadence-label">
            <button
              type="button"
              aria-pressed={billing === "monthly"}
              onClick={() => onBillingChange("monthly")}
              disabled={!editable}
            >
              Monthly
            </button>
            <button
              type="button"
              aria-pressed={billing === "annual"}
              onClick={() => onBillingChange("annual")}
              disabled={!editable}
            >
              Annual
              <span className="eg-order-save">2 months free</span>
            </button>
          </div>
        </div>
      )}

      {/* ---- the detail ---- */}
      <dl className="eg-order-rows">
        {/* "Capacity", not "capacity commitment". The panel is 21rem wide and
            the long form pushed "More than 1,000 students" onto two lines on
            the quoted band. The full term is used on the review recap, where
            there is room for it. */}
        <Row label="Capacity" value={band.name} />
        {/* Monthly on the card path is month to month (Sep 15 2026); a quote is annual. */}
        <Row label="Term" value={path === "card" && billing === "monthly" ? "Month to month" : `${TERM_MONTHS} months`} />
        {estimatedStudents === null ? null : (
          <Row label="Program size you gave" value={`${formatCount(estimatedStudents)} students`} />
        )}
        <Row
          label="Starts"
          value={path === "card" ? "When payment confirms" : "The day we invoice"}
        />
        <Row label="Sales tax" value={salesTaxLine(path)} />
      </dl>

      <div className="eg-order-total">
        <dt>{path === "card" ? "Charged today" : "Due today"}</dt>
        <dd>{chargeToday(band, billing, path)}</dd>
      </div>

      <p className="eg-order-note">
      </p>

      {editable ? (
        <a className="eg-order-change" href="/pricing">
          Change your capacity
        </a>
      ) : null}
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="eg-order-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * The sales tax row. On the card path it said "Added at payment" until Sep 14
 * 2026, which was false: Stripe Tax is off until Michael Beckman answers, and
 * the eight Stripe Prices are tax exclusive, so nothing is added at payment.
 * Thomas's ruling is to say the price excludes sales tax.
 */
export function salesTaxLine(path: CheckoutPath): string {
  return path === "card" ? "Excluded from the price" : "Shown on the quote";
}

/**
 * The real number. On the quote path nothing is charged, and saying "$0" there
 * would be a figure where the truth is an absence, so it says so in words.
 */
export function chargeToday(
  band: CapacityBand,
  billing: Billing,
  path: CheckoutPath,
): string {
  if (path === "quote") return "Nothing";
  if (band.monthly === null || band.annual === null) return "Priced on your cohort";
  return billing === "annual" ? formatMoney(band.annual) : formatMoney(band.monthly);
}
